import React, { useEffect, useState } from 'react';
import yup from '../../../libs/yup/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { searchTenantCosts, addGuestCost } from '../../../api/sportaloka-client';
import Swal from 'sweetalert2';
import { useParams } from 'react-router-dom';

const schema = yup.object().shape({
  items: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().required('ID is required'),
        label: yup.string(),
        price: yup.number(),
        quantity: yup.number().min(0, 'Quantity cannot be negative')
      })
    )
    .required('Items array is required')
    .test(
      'at-least-one-quantity-greater-than-zero',
      'At least one item must have a quantity greater than 0',
      (items) => items.some((item) => item.quantity > 0)
    )
});
const defaultValues = {
  items: []
};

const AddCostModal = ({ guestId, resetData }) => {
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);

  const onSubmit = async (values) => {
    try {
      setLoading(true);
      const filteredValues = values.items.filter((item) => item.quantity > 0);
      const payload = filteredValues.map((item) => {
        return {
          tenant_cost_id: item.id,
          quantity: item.quantity
        };
      });
      await addGuestCost({ items: payload }, guestId);
      Swal.fire({
        title: 'Berhasil!',
        text: `Data berhasil ditambahkan`,
        icon: 'success',
        confirmButtonText: 'Tutup'
      });
      resetData();
      document.getElementById('closeModalGuestCost').click();

      setLoading(false);
    } catch (error) {
      setLoading(false);
      Swal.fire({
        title: 'Gagal!',
        text: 'Internal Server Error',
        icon: 'error',
        confirmButtonText: 'Tutup'
      });
    }
  };

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid }
  } = useForm({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(schema)
  });

  const { fields } = useFieldArray({
    control,
    name: 'items'
  });

  const getTenantCosts = async () => {
    try {
      setFetchLoading(true);
      setFetchError(false);
      const query = new URLSearchParams({
        page: 1,
        limit: 10000000
      }).toString();
      const response = await searchTenantCosts(query);
      const responseData = response.data.data.data;
      reset({
        items: responseData.map((cost) => ({
          id: cost.id,
          label: cost.description,
          price: cost.amount,
          quantity: 0
        }))
      });
      setFetchLoading(false);
    } catch (error) {
      setFetchLoading(false);
      setFetchError(true);
    }
  };

  useEffect(() => {
    getTenantCosts();
    const handleModalHidden = () => {
      reset();
    };

    const modalElement = document.getElementById('addCostModal');
    modalElement.addEventListener('hidden.bs.modal', handleModalHidden);

    return () => {
      modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
    };
  }, []);

  return (
    <div className="modal" tabIndex="-1" id="addCostModal">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-header">
              <h5 className="modal-title fw-600 text-16">Tambahkan Biaya</h5>
              <button
                id="closeModalGuestCost"
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <table className="table">
                <thead className="bg-blue-1 text-white">
                  <tr>
                    <th
                      className="bg-blue-1 rounded-0 text-white fw-700 py-3 px-3 text-14"
                      width="5%"></th>
                    <th
                      className="bg-blue-1 rounded-0 text-white fw-700 py-3 px-3 text-14"
                      width="65%">
                      Nama Item
                    </th>
                    <th
                      className="bg-blue-1 rounded-0 text-white fw-700 py-3 px-3 text-14 text-right"
                      width="30%">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fetchLoading && !fetchError && (
                    <tr>
                      <th className="py-3 px-3 text-14 text-center" colSpan={3}>
                        Loading...
                      </th>
                    </tr>
                  )}
                  {!fetchLoading && fetchError && (
                    <tr>
                      <th className="py-3 px-3 text-14 text-center" colSpan={3}>
                        Gagal mendapatkan data{' '}
                        <span className="cursor-pointer text-danger" onClick={getTenantCosts}>
                          Coba lagi
                        </span>
                      </th>
                    </tr>
                  )}
                  {!fetchLoading && !fetchError && fields.length === 0 && (
                    <tr>
                      <th className="py-3 px-3 text-14 text-center" colSpan={3}>
                        Data Tidak Ditemukan
                      </th>
                    </tr>
                  )}
                  {!fetchLoading &&
                    !fetchError &&
                    fields.length > 0 &&
                    fields.map((item, index) => (
                      <tr key={item.id}>
                        <td className="py-2 px-3 text-14" width="5%">
                          <Controller
                            name={`items[${index}].quantity`}
                            control={control}
                            render={({ field }) => (
                              <div className="form-checkbox d-flex items-center">
                                <input
                                  type="checkbox"
                                  checked={field.value > 0}
                                  onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                                />
                                <div className="form-checkbox__mark">
                                  <div className="form-checkbox__icon icon-check" />
                                </div>
                                {/* <div className="form-checkbox__mark" /> */}
                              </div>
                            )}
                          />
                        </td>
                        <td className="py-2 px-3 text-14" width="65%">
                          {item.label}
                        </td>
                        <td className="py-2 px-3 text-14 text-right" width="30%">
                          IDR {item.price.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer d-flex justify-content-center">
              <button
                disabled={loading || !isValid}
                type="submit"
                className="btn btn-primary bg-blue-1 rounded-1 fw-600 text-14">
                {loading ? 'Loading...' : 'Tambahkan'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCostModal;
